// @ts-nocheck
import { Entity, liveQuery } from 'dexie';
import { BehaviorSubject, from } from 'rxjs';
import { interval } from '../utils/interval';
import type { QueryParam, QueryParams } from './api/ApiService';
import ApiV1Adapter from './api/ApiV1Adapter';
import { db, type AppDB, type EntitiesTable } from './db';
import type Configuration from './models/Configuration';

export const POLLING_DEFAULT_CONFIG = {
  duration: 1000 * 60 * 10, // 10 minutes
  retrieveDelta: false,
};

type PollingConfiguration = { duration: number; retrieveDelta?: boolean };

type ConstructorArgs<T, U> = {
  name: T;
  polling?: PollingConfiguration | false;
  model: Function;
  queryParams?: QueryParams<any>;
  synchronizeOnStart?: boolean;
  /**
   * this paging option allow to fetch pull times under a condition data from api which have massive data (e.g landmarks, missions...).
   * The return value of the function is the queryParams which will be used on the next fetch.
   * Example : 
   * (landmarks) => {
        // verifying if landmarks have more than 5000 entries
        if (landmarks.length >= 5000) {
          const lastLandmark = landmarks.at(-1);
          // return what we pass into queryParams to the next fetch.
          return { poi: lastLandmark.uid, timestamp: lastLandmark.timestamp };
        }
        // return false to not fetch again the entity.
        return false;
      }
   */
  paging?: false | ((values: U[]) => QueryParams<any> | false);
};

/**
 * Class which manage an entity of Dexie database.
 *
 * This the core system of data management of the application.
 * Manage the optimistic update, the polling with real database
 * wrapped under CRUD operations.
 */
abstract class EntityManager<
  T extends Entity<AppDB>, // The entity's model
  U extends keyof EntitiesTable, // The name of the table (need to be the name of api's endpoint)
  V extends QueryParam = {}, // Query params send to api if needed.
> {
  public name!: U;
  public polling$ = new BehaviorSubject<{ duration: number; retrieveDelta?: boolean }>(
    POLLING_DEFAULT_CONFIG
  );
  public queryParams!: V;
  public isInit = false;
  public onInit = () => {};

  private _synchronizeOnStart = true;
  private _paging = false;
  private _apiService: ApiV1Adapter<T, V>;
  private _dbOperation!: () => Promise<number | void>;
  private _apiOperation!: (id: number | null) => Promise<T | void>;
  private _pollingIntervalId: NodeJS.Timeout;

  constructor({
    name,
    polling = POLLING_DEFAULT_CONFIG,
    model,
    queryParams = {},
    synchronizeOnStart = true,
    paging = false,
  }: ConstructorArgs<U, T>) {
    if (polling) {
      this.polling$.next({ ...POLLING_DEFAULT_CONFIG, ...polling });
    }

    this.name = name;
    this.table.mapToClass(model);
    this.queryParams = queryParams;
    this._synchronizeOnStart = synchronizeOnStart;
    //@ts-ignore
    this._paging = paging;
  }

  public init() {
    this._apiService = new ApiV1Adapter<T, V>(this.name);
    this.isInit = true;
    this.onInit();
    if (this._synchronizeOnStart) {
      if (this.polling$.value) this._poll();
      else this._synchronize();
    }
  }

  public clean() {
    this.isInit = false;
  }

  /**
   * Get the entity table of Dexie to make directly operations on it.
   */
  get table() {
    return db[this.name];
  }

  /**
   * Retrieve all values as an observable.
   *
   * @returns Observable of an entity.
   */
  public $getAll() {
    return liveQuery(() => this.getAll());
  }

  /**
   * Retrieve on value by id as an observable.
   *
   * @returns Observable of one value of entity.
   */
  public getById$(id: number | null) {
    return from(
      liveQuery(() => {
        if (id) return this.getById(id);
        return null;
      })
    );
  }

  /**
   * Retrieve all values of the entity.
   *
   * @returns An array of values (e.g an array of landmarks).
   */
  public async getAll(): Promise<T[]> {
    return this.table.toArray();
  }

  /**
   * Retrieve on value by an id.
   *
   * @returns Observable of a value of entity.
   */
  public async getById(id: number): Promise<T> {
    if (!id) return null;
    return db[this.name].get(id);
  }

  /**
   * Manage CRUD operations in a optimistic way (Not implemented yet).
   *
   * @returns The notification to send to user.
   */
  private async _optimisticUpdate(action: 'CREATE' | 'UPDATE' | 'DELETE') {
    const id = await this._dbOperation();

    let res: ReturnType<typeof this._apiOperation>;

    try {
      res = await this._apiOperation();
    } catch (error) {
      console.error('An error append on entity manager operation:', error);
    }

    if (action === 'CREATE' && res.insertedId !== id) {
      await this.table.update(id, { uid: res.insertedId });
    }

    return res;
  }

  public async pull({ queryParams = {} } = { queryParams: {} }) {
    const result = await this._apiService.get({
      queryParams,
    });

    if (this.polling$.value.retrieveDelta) {
      this.table.bulkPut(result);
    } else {
      // Using a transaction here to avoid double trigger to livequery due to two operations clear() and bulkAdd() (using waitFor())
      await db.transaction('rw', this.table, async transaction => {
        // Need to clear all table because we don't have delete local strategy for deleted items from api.
        // (e.g a unit deleted by a person on his application, not appear anymore into api and the bulk put do not make the job for this).
        await this.table.clear();
        await transaction.waitFor(this.table.bulkAdd(result));
      });
    }

    if (this._paging) {
      const pagingCondition = this._paging(result);
      if (pagingCondition) this.pull({ queryParams: pagingCondition });
    }
  }

  /**
   * Pull the fresh data and put it into local database.
   * Manage polling if needed.
   */
  private async _synchronize() {
    let configuration: Configuration | undefined;

    if (this.polling$.value) configuration = await db._configuration.where('name').equals(this.name).first();

    await this.pull({
      queryParams: { timestamp: configuration?.polling || 0, ...this.queryParams },
    });

    const needPooling = this.polling$.value && !!this._apiService.polling_timestamp;

    if (needPooling)
      db._configuration.put({
        uid: configuration?.uid,
        name: this.name,
        polling: this._apiService.polling_timestamp,
      });
  }

  /**
   * Call synchronize method according to polling configuration.
   */
  private async _poll() {
    this._synchronize();
    this.polling$.subscribe(() => {
      if (this._pollingIntervalId) interval.clear(this._pollingIntervalId);

      this._pollingIntervalId = interval.set(() => this._synchronize(), this.polling$.value.duration);
    });
  }

  public updatePolling(updatedValue: PollingConfiguration) {
    this.polling$.next({ ...this.polling$.value, ...updatedValue });
  }

  /**
   * Create a new value of an entity.
   *
   * @param entityValue - The new value (e.g values of a new landmark).
   */
  public async create(entityValue: T) {
    const entityValueCopy = { ...entityValue };

    this._dbOperation = async () => this.table.add(entityValue);

    this._apiOperation = () => this._apiService.post(entityValue);

    const res = await this._optimisticUpdate('CREATE');

    return res.insertedId;
  }

  /**
   * Update value of an entity.
   *
   * @param entityValue - The update keys of a value (not need to send all value, just the updated one).
   */
  public async update(uid: number, entityValue: T) {
    const previousEntity = await this.table.get(uid);

    this._dbOperation = () => this.table.update(uid, entityValue);
    this._apiOperation = () => this._apiService.put({ ...entityValue, uid });
    return this._optimisticUpdate('UPDATE');
  }

  /**
   * Delete value of an entity.
   *
   * @param id - ID of entity value to delete.
   */
  public async delete(id: number) {
    this._dbOperation = () => this.table.delete(id);
    this._apiOperation = () => this._apiService.delete(id);

    await this._optimisticUpdate('DELETE');
  }
}

export default EntityManager;
